import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Typography from 'components/ui-kit/typography';
import Money from 'components/ui-kit/money';
import { useApp } from 'hooks';
import { creditWithTip } from 'helpers/calculations.helper';
import {
  createEmptyTipData,
  DEFAULT_PRESET_PERCENTAGES,
  getPresetTipPercentages,
} from 'helpers/payment-display.helper';

import { FlexBox } from 'components/ui-kit/flex/style';
import * as S from './styles';

import {
  formatTipAmount,
  getTipValidationError,
  normalizeCustomTipAmount,
} from './utils';

const TIP_TYPES = {
  percentage: 1,
  customAmount: 2,
};

function TipSelector({
  totalAmount,
  tipBaseAmount,
  creditApplied,
  onTipChange,
  onTipValidationChange,
  presetPercentages,
  showHeader,
}) {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [tipAmount, setTipAmount] = useState(0);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const { country } = useApp();

  const effectivePercentages = getPresetTipPercentages(presetPercentages)
    .filter((pct) => Number.isFinite(pct) && pct > 0)
    .map((pct) => formatTipAmount(Number(pct)));
  const percentageBaseAmount = Math.max(tipBaseAmount ?? totalAmount, 0);
  const hasCustomInputValue = showCustomInput && customTip !== '';
  const tipValidationError = hasCustomInputValue ? getTipValidationError(tipAmount) : null;
  const credit = creditWithTip(creditApplied, totalAmount, tipAmount, country);

  useEffect(() => {
    onTipValidationChange(!tipValidationError);
  }, [tipValidationError, onTipValidationChange]);

  const handlePresetClick = (percentage) => {
    if (selectedPreset === percentage) {
      setSelectedPreset(null);
      setCustomTip('');
      setTipAmount(0);
      setShowCustomInput(false);
      onTipChange(createEmptyTipData());
    } else {
      setSelectedPreset(percentage);
      setCustomTip('');
      setShowCustomInput(false);
      const calculated = (percentageBaseAmount * percentage) / 100;
      const rounded = formatTipAmount(calculated);
      setTipAmount(rounded);
      onTipChange({
        tipAmount: rounded,
        tipType: TIP_TYPES.percentage,
        tipPercentage: percentage,
      });
    }
  };

  const handleCustomClick = () => {
    setShowCustomInput(true);
    setSelectedPreset(null);
    if (!customTip) {
      setTipAmount(0);
      onTipChange(createEmptyTipData());
    }
  };

  const handleCustomChange = (event) => {
    const { value } = event.target;

    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setCustomTip(value);
      const numValue = normalizeCustomTipAmount(value);
      setTipAmount(numValue);
      onTipChange({
        tipAmount: numValue,
        tipType: numValue > 0 ? TIP_TYPES.customAmount : null,
        tipPercentage: null,
      });
    }
  };

  return (
    <S.TipContainer>
      {showHeader && (
        <Typography fontSize={14} color="grey">
          Add tip (optional)
        </Typography>
      )}

      <S.PresetButtons>
        {effectivePercentages.map((pct) => (
          <S.TipButton
            key={pct}
            size="small"
            variant="outlined"
            $active={selectedPreset === pct}
            onClick={() => handlePresetClick(pct)}
          >
            <span>{`${pct}%`}</span>
            <S.AmountHint>
              <Money amount={formatTipAmount((percentageBaseAmount * pct) / 100)} />
            </S.AmountHint>
          </S.TipButton>
        ))}

        <S.TipButton
          size="small"
          variant="outlined"
          $active={showCustomInput}
          onClick={handleCustomClick}
        >
          Custom
        </S.TipButton>
      </S.PresetButtons>

      {showCustomInput && (
        <S.CustomRow>
          <S.TipInput
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={customTip}
            onChange={handleCustomChange}
            autoFocus
          />
          <Typography color="grey">USD</Typography>
        </S.CustomRow>
      )}

      {tipAmount > 0 && (
        <FlexBox direction="column" gap="small">
          <Typography as="div" fontSize={13} fontFamily="regular" color="grey">
            Tip amount:
            {' '}
            <S.SummaryAmount>
              <Money amount={tipAmount} />
            </S.SummaryAmount>
          </Typography>

          <Typography as="div" fontSize={13} fontFamily="regular" color="grey">
            Amount to pay:
            {' '}
            <S.SummaryAmount>
              <Money amount={totalAmount + tipAmount - credit} />
            </S.SummaryAmount>
          </Typography>
        </FlexBox>

      )}

      {tipValidationError && (
        <S.ValidationError>{tipValidationError}</S.ValidationError>
      )}
    </S.TipContainer>
  );
}

TipSelector.propTypes = {
  totalAmount: PropTypes.number.isRequired,
  tipBaseAmount: PropTypes.number,
  creditApplied: PropTypes.number,
  onTipChange: PropTypes.func.isRequired,
  onTipValidationChange: PropTypes.func,
  presetPercentages: PropTypes.arrayOf(PropTypes.number),
  showHeader: PropTypes.bool,
};

TipSelector.defaultProps = {
  tipBaseAmount: null,
  creditApplied: 0,
  onTipValidationChange: () => {},
  presetPercentages: DEFAULT_PRESET_PERCENTAGES,
  showHeader: true,
};

export default TipSelector;
